import React, { useEffect, useRef, useState } from 'react';
import './isaAllowance.scss';
import infoIcon from '../../../../assets/images/info-icon.svg';
import { getCurrentUKTaxYear, formatISAAmount } from '../../../../utils/taxYearHelpers';
import { useUnifiedAnalytics } from '../../../../contexts/AnalyticsWrapper';

/**
 * @typedef {Object} IsaAllowanceProps
 * @property {number} [totalAllowance=20000] - Total ISA allowance amount
 * @property {number} [usedAmount=0] - Amount already used/deposited
 * @property {number} [remainingAllowance] - Amount remaining in the ISA allowance
 * @property {string} [taxYear] - Current tax year string (e.g. "2023/2024")
 */

/** @type {React.FC<IsaAllowanceProps>} */
const IsaAllowance = ({ totalAllowance = 20000, usedAmount = 0, remainingAllowance, taxYear}) => {
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const infoBtnRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const infoPopupRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const analytics = useUnifiedAnalytics();
  const safeTotal = Math.max(0, Number(totalAllowance) || 0);
  const safeUsedRaw = Math.max(0, Number(usedAmount) || 0);
  const safeUsed = Math.min(safeUsedRaw, safeTotal);
  const remainingAmount = Math.max(0, safeTotal - safeUsed);
  const usagePercentage = safeTotal > 0 ? Math.min(100, Math.max(0, (safeUsed / safeTotal) * 100)) : 0;
  const currentTaxYear = taxYear;
  const tooltipTaxYear = currentTaxYear
    ? `${currentTaxYear.split('-')[0]}/${currentTaxYear.split('-')[1]?.slice(-2)}`
    : '';

  const handleInfoClick = () => {
    // Track analytics for tooltip click
    analytics.trackAnalyticsClickEvent(
      "ISA allowance info tooltip",
      window.location.href,
      "ISA allowance component",
      "",
      "",
      {},
      showInfoPopup ? "click" : "click",
      `dashboard|click|isa-allowance-tooltip`,
      "ISA Allowance Feature"
    );
    
    setShowInfoPopup((prev) => !prev);
  };

  // Close on outside click
  useEffect(() => {
    if (!showInfoPopup) return;
    /** @param {MouseEvent} e */
    const onDocClick = (e) => {
      const btn = infoBtnRef.current;
      const popup = infoPopupRef.current;
      if (!btn || !popup) return;
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (btn.contains(target) || popup.contains(target)) return;
      setShowInfoPopup(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showInfoPopup]);

  // Close on Escape
  useEffect(() => {
    if (!showInfoPopup) return;
    /** @param {KeyboardEvent} e */
    const onKey = (e) => {
      if (e.key === 'Escape') setShowInfoPopup(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showInfoPopup]);

  // Use remainingAllowance from props if provided, otherwise calculate from totalAllowance - usedAmount
  const finalRemainingAllowance = typeof remainingAllowance === 'number'
    ? Math.max(0, Number(remainingAllowance))
    : remainingAmount;

  return (
    <div className="isa-allowance">
      <div className="isa-allowance__content">
        <div className="isa-allowance__header">
          <span className="isa-allowance__label">ISA allowance {currentTaxYear}</span>
          <button
            className="isa-allowance__info-icon"
            onClick={handleInfoClick}
            aria-label="ISA allowance information"
            aria-expanded={showInfoPopup}
            aria-controls="isa-allowance-info"
            ref={infoBtnRef}
            type="button"
          >
            <img src={infoIcon} alt="ISA allowance information" />
          </button>
          {showInfoPopup && (
            <div
              className="isa-allowance__info-popup"
              id="isa-allowance-info"
              role="tooltip"
              ref={infoPopupRef}
            >
              Your ISA allowance is the total amount you can save tax-free across all your ISAs in a tax year. For {tooltipTaxYear}, the limit is £20,000. This tracker only shows what you&apos;ve saved with us, so if you also have ISAs elsewhere, you&apos;ll need to keep track of those to stay within your total allowance.
            </div>
          )}
        </div>
        <div className="isa-allowance__amount">
          You can still deposit {formatISAAmount(finalRemainingAllowance)}
        </div>
        <div className="isa-allowance__progress">
          <div className="isa-allowance__progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(usagePercentage)}>
            <div className="isa-allowance__progress-indicator" style={{ width: `${usagePercentage}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IsaAllowance;
